"use client";

import {
    Dubbings,
    Voice,
    voices,
    TTSModel,
    ttsModels,
    defaultVoice,
    defaultTTSModel,
    getVoiceLabel,
} from "@/app/api/[[...route]]/services/ai/ai-video-workflow/types";
import { Button, buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { client } from "@/lib/hono";
import { cn } from "@/lib/utils";
import { LoaderCircle, Settings, Sparkles } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

const MAX_TEXT_CHARS = 200;

export default function VoicesPlaygroundModal({
    text,
    setText,
    dubbings,
    setDubbings,
}: {
    text: string;
    setText: Dispatch<SetStateAction<string>>;
    dubbings: Dubbings;
    setDubbings: (dubbings: Dubbings) => void;
}) {
    const [model, setModel] = useState<TTSModel>(
        dubbings.model || defaultTTSModel
    );
    const [voice, setVoice] = useState<Voice>(dubbings.voice || defaultVoice);
    const [instructions, setInstructions] = useState(
        dubbings.instructions || ""
    );
    const [loading, setLoading] = useState(false);
    const { dir } = useLocale();

    // Blob URL of returned audio
    const [outputAudio, setOutputAudio] = useState<string | null>(null);

    useEffect(() => {
        setModel(dubbings.model || defaultTTSModel);
    }, [dubbings.model]);
    useEffect(() => {
        setVoice(dubbings.voice || defaultVoice);
    }, [dubbings.voice]);
    useEffect(() => {
        setInstructions(dubbings.instructions || "");
    }, [dubbings.instructions]);

    // Revoke old Blob URLs
    useEffect(() => {
        return () => {
            if (outputAudio) URL.revokeObjectURL(outputAudio);
        };
    }, [outputAudio]);

    const generateDubbings = async () => {
        if (!text.trim()) {
            toast.error(
                t("funnels.ai-upload-videos.dubbings-playground.error.no-text")
            );
            return;
        }

        setLoading(true);

        // clear previous audio URL
        if (outputAudio) {
            URL.revokeObjectURL(outputAudio);
            setOutputAudio(null);
        }

        try {
            const res = await client.api.ai["test-dubbings"].$post({
                json: { text, model, voice, instructions },
            });

            if (!res.ok) {
                toast.error(
                    t(
                        "funnels.ai-upload-videos.dubbings-playground.error.unexpected"
                    )
                );
            } else {
                const blob = await res.blob(); // should be audio/wav
                const url = URL.createObjectURL(blob);
                setOutputAudio(url);
            }
        } catch {
            // server rethrows; we just show a generic error
            toast.error(
                t("funnels.ai-upload-videos.dubbings-playground.error.unexpected")
            );
        } finally {
            setLoading(false);
        }
    };

    const confirmSettings = () => {
        setDubbings({
            ...dubbings,
            model,
            voice,
            instructions,
        });
    };

    return (
        <Dialog>
            <DialogTrigger
                className={cn(
                    buttonVariants({
                        variant: "link",
                    }),
                    "py-0 px-1 font-normal h-5 gap-1"
                )}
            >
                <Settings className="size-4" />
                {t("funnels.ai-upload-videos.dubbings-playground.trigger")}
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {t(
                            "funnels.ai-upload-videos.dubbings-playground.title"
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        {t(
                            "funnels.ai-upload-videos.dubbings-playground.description"
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <div className="flex">
                        <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                            {t(
                                "funnels.ai-upload-videos.dubbings-playground.text.label"
                            )}
                            <span className="ms-1 font-mono text-xs">
                                ({text.length}/{MAX_TEXT_CHARS}):
                            </span>
                        </div>
                        <Input
                            value={text}
                            onChange={(e) => {
                                const newText = e.target.value;
                                if (newText.length <= MAX_TEXT_CHARS) {
                                    setText(e.target.value);
                                }
                            }}
                            className="rounded-s-none border-s-0"
                        />
                    </div>
                    <Select
                        value={model}
                        onValueChange={(val) => setModel(val as TTSModel)}
                        dir={dir}
                    >
                        <div className="mt-2.5 flex">
                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                {t(
                                    "funnels.ai-upload-videos.dubbings-playground.model.label"
                                )}
                            </div>

                            <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                <SelectValue />
                            </SelectTrigger>
                        </div>

                        <SelectContent>
                            {ttsModels.map((model, idx) => (
                                <SelectItem key={idx} value={model}>
                                    {model}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        value={voice}
                        onValueChange={(val) => setVoice(val as Voice)}
                        dir={dir}
                    >
                        <div className="mt-2.5 flex">
                            <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                                {t(
                                    "funnels.ai-upload-videos.dubbings-playground.voice.label"
                                )}
                            </div>

                            <SelectTrigger className="rounded-s-none border-s-0 h-9">
                                <SelectValue />
                            </SelectTrigger>
                        </div>

                        <SelectContent>
                            {voices.map((voice, idx) => (
                                <SelectItem key={idx} value={voice}>
                                    {getVoiceLabel(voice)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="mt-2.5 flex">
                        <div className="shrink-0 flex justify-center items-center bg-muted text-sm h-9 border border-input px-2.5 rounded-s-md">
                            {t(
                                "funnels.ai-upload-videos.dubbings-playground.instructions.label"
                            )}
                        </div>
                        <Input
                            placeholder={t(
                                "funnels.ai-upload-videos.dubbings-playground.instructions.placeholder"
                            )}
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            className="rounded-s-none border-s-0"
                        />
                    </div>
                    <Button
                        disabled={loading}
                        onClick={generateDubbings}
                        variant="ai"
                        size="sm"
                        className="mt-4 flex flex-row items-center gap-2"
                    >
                        {loading ? (
                            <LoaderCircle className="size-4 animate-spin" />
                        ) : (
                            <>
                                <Sparkles className="size-4" />
                                {t(
                                    "funnels.ai-upload-videos.dubbings-playground.generate"
                                )}
                            </>
                        )}
                    </Button>
                    {outputAudio && (
                        <audio
                            key={outputAudio}
                            src={outputAudio}
                            controls
                            className="mt-4 w-full"
                        />
                    )}
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">{t("ui.close")}</Button>
                    </DialogClose>
                    <DialogClose asChild>
                        <Button onClick={confirmSettings}>
                            {t(
                                "funnels.ai-upload-videos.dubbings-playground.confirm"
                            )}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
